import { useMemo, type FunctionComponent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Skeleton, Space, Tooltip, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";

import {
  HabitCompletionStatus,
  isHabitScheduledOn,
  type HabitSchedule,
  type HabitWeekday,
} from "@fitness-app/data-models/entities/Habit";
import {
  calculateHabitCompletionRate,
  habitScheduledDatesInRange,
} from "@fitness-app/data-models/domain/services/habitCompliance";

import { useTraineeId } from "~/modules/Trainee/TraineeProfile/scope";
import { supabase } from "~/store/initializeStore";
import { challengesHabitsKeys } from "./queryKeys";

// TRE-1579 — per-habit compliance detail for the trainer: a 2-month completion calendar
// (completed / partial / skipped vs scheduled), monthly completion % against SCHEDULED days,
// total completed count and the longest streak. Rendered lazily inside the table's expanded row.

const CELL = 22;

const WEEKDAY_HEADERS = ["Pn", "Wt", "Śr", "Cz", "Pt", "So", "Nd"];

type CompletionRow = {
  date: string;
  status: string;
};

type Props = {
  clientHabitId: string;
  schedule: HabitSchedule | null;
  startDate: string | null;
  longestStreak: number;
};

const cellStyle = (background: string, options?: { border?: string; color?: string }): React.CSSProperties => ({
  alignItems: "center",
  backgroundColor: background,
  border: options?.border ? `1px solid ${options.border}` : "1px solid transparent",
  borderRadius: 4,
  color: options?.color ?? "inherit",
  display: "flex",
  fontSize: 10,
  height: CELL,
  justifyContent: "center",
  width: CELL,
});

const MonthGrid: FunctionComponent<{
  month: Dayjs;
  schedule: HabitSchedule | null;
  startBound: string | null;
  statusByDate: Map<string, string>;
}> = ({ month, schedule, startBound, statusByDate }) => {
  const today = dayjs().format("YYYY-MM-DD");
  const daysInMonth = month.daysInMonth();
  // Monday-first column index for the 1st of the month.
  const leadingBlanks = (month.date(1).day() + 6) % 7;

  const cells: React.ReactNode[] = [];

  // Shared domain math for the % — the cell loop below is purely visual.
  const monthEnd = month.endOf("month").format("YYYY-MM-DD");
  const scheduledDates = habitScheduledDatesInRange({
    schedule,
    startDate: startBound,
    from: month.format("YYYY-MM-DD"),
    to: monthEnd < today ? monthEnd : today,
  });
  const {
    completed: completedCount,
    scheduled: scheduledCount,
    rate: percent,
  } = calculateHabitCompletionRate(
    scheduledDates,
    [...statusByDate.entries()]
      .filter(([, status]) => status === HabitCompletionStatus.Completed)
      .map(([date]) => date),
  );

  for (let blank = 0; blank < leadingBlanks; blank += 1) {
    cells.push(<div key={`blank-${blank}`} style={{ height: CELL, width: CELL }} />);
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = month.date(day);
    const dateStr = date.format("YYYY-MM-DD");
    const weekday = date.day() as HabitWeekday;
    const scheduled = schedule ? isHabitScheduledOn(schedule, weekday) : true;
    const beforeStart = startBound != null && dateStr < startBound;
    const inPast = dateStr <= today;
    const status = statusByDate.get(dateStr);

    let style: React.CSSProperties;
    let tooltip: string;
    if (status === HabitCompletionStatus.Completed) {
      style = cellStyle("#52c41a", { color: "#fff" });
      tooltip = "Wykonane";
    } else if (status === HabitCompletionStatus.Partial) {
      style = cellStyle("#faad14", { color: "#fff" });
      tooltip = "Częściowo";
    } else if (status === HabitCompletionStatus.Skipped) {
      style = cellStyle("#d9d9d9", { color: "#8c8c8c" });
      tooltip = "Pominięte";
    } else if (scheduled && !beforeStart && inPast) {
      style = cellStyle("transparent", { border: "#ff7875", color: "#bfbfbf" });
      tooltip = "Zaplanowane — brak wpisu";
    } else if (scheduled && !beforeStart) {
      style = cellStyle("transparent", { border: "#d9d9d9", color: "#bfbfbf" });
      tooltip = "Zaplanowane";
    } else {
      style = cellStyle("transparent", { color: "#d9d9d9" });
      tooltip = "Poza harmonogramem";
    }

    cells.push(
      <Tooltip key={dateStr} title={`${date.format("DD.MM")} — ${tooltip}`}>
        <div style={style}>{day}</div>
      </Tooltip>,
    );
  }

  return (
    <Space direction="vertical" size={6}>
      <Space size={8}>
        <Typography.Text strong style={{ textTransform: "capitalize" }}>
          {month.locale("pl").format("MMMM YYYY")}
        </Typography.Text>
        <Typography.Text type="secondary">
          {percent != null
            ? `${percent}% (${completedCount}/${scheduledCount} zaplanowanych)`
            : "brak zaplanowanych dni"}
        </Typography.Text>
      </Space>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(7, ${CELL}px)`, gap: 3 }}>
        {WEEKDAY_HEADERS.map((header) => (
          <div key={header} style={{ fontSize: 10, textAlign: "center", color: "#8c8c8c" }}>
            {header}
          </div>
        ))}
        {cells}
      </div>
    </Space>
  );
};

const HabitComplianceCalendar: FunctionComponent<Props> = ({ clientHabitId, schedule, startDate, longestStreak }) => {
  const traineeId = useTraineeId();
  const rangeStart = useMemo(() => dayjs().subtract(1, "month").startOf("month").format("YYYY-MM-DD"), []);

  // Lazy: this query only runs when the row is expanded (the component mounts then).
  const completionsQuery = useQuery({
    queryKey: challengesHabitsKeys.habitCompliance(traineeId, clientHabitId, rangeStart),
    queryFn: async () => {
      const [{ data, error }, { count, error: countError }] = await Promise.all([
        supabase
          .from("habit_completion")
          .select("date, status")
          .eq("clientHabitId", clientHabitId)
          .gte("date", rangeStart)
          .returns<CompletionRow[]>(),
        supabase
          .from("habit_completion")
          .select("id", { count: "exact", head: true })
          .eq("clientHabitId", clientHabitId)
          .eq("status", HabitCompletionStatus.Completed),
      ]);

      if (error || countError) {
        throw new Error(error?.message ?? countError?.message);
      }

      return { rows: data ?? [], totalCompleted: count ?? 0 };
    },
  });

  const statusByDate = useMemo(
    () => new Map((completionsQuery.data?.rows ?? []).map((row) => [row.date, row.status])),
    [completionsQuery.data],
  );

  if (completionsQuery.isLoading) {
    return <Skeleton active paragraph={{ rows: 3 }} />;
  }

  const startBound = startDate ? startDate.slice(0, 10) : null;
  const previousMonth = dayjs().subtract(1, "month").startOf("month");
  const currentMonth = dayjs().startOf("month");

  return (
    <Space direction="vertical" size={12} style={{ padding: "4px 8px" }}>
      <Space size={16}>
        <Typography.Text>
          Wykonane łącznie: <Typography.Text strong>{completionsQuery.data?.totalCompleted ?? 0}</Typography.Text>
        </Typography.Text>
        <Typography.Text>
          Najdłuższa seria: <Typography.Text strong>{longestStreak}</Typography.Text>
        </Typography.Text>
      </Space>
      <Space size={32} align="start" wrap>
        <MonthGrid month={previousMonth} schedule={schedule} startBound={startBound} statusByDate={statusByDate} />
        <MonthGrid month={currentMonth} schedule={schedule} startBound={startBound} statusByDate={statusByDate} />
      </Space>
      <Space size={12} wrap>
        <LegendItem color="#52c41a" label="Wykonane" />
        <LegendItem color="#faad14" label="Częściowo" />
        <LegendItem color="#d9d9d9" label="Pominięte" />
        <LegendItem color="transparent" border="#ff7875" label="Zaplanowane — brak wpisu" />
      </Space>
    </Space>
  );
};

const LegendItem: FunctionComponent<{ color: string; label: string; border?: string }> = ({ color, label, border }) => (
  <Space size={4}>
    <div
      style={{
        backgroundColor: color,
        border: `1px solid ${border ?? color}`,
        borderRadius: 3,
        height: 10,
        width: 10,
      }}
    />
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      {label}
    </Typography.Text>
  </Space>
);

export default HabitComplianceCalendar;
